import { useMemo, useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { IncidentPosition } from 'hooks/incident/useRoster';
import { ICS204_LABEL, ICS204_OUTLINE_BTN } from './assignmentCardStyles';
import { Ics204SelectField, type Ics204SelectOption } from './Ics204Fields';

const ADD_NEW_VALUE = '__add_new__';

type LocationCategory = 'Branch' | 'Division' | 'Group' | 'Staging Area';

type Ics204LocationSelectProps = {
  label: string;
  fieldName: string;
  testId: string;
  category: LocationCategory;
  positions: IncidentPosition[];
  className?: string;
};

/**
 * Assigned Location select: roster seats with the matching category, plus "+
 * Add New" free text stored only on this ICS-204 (not on the roster).
 */
const Ics204LocationSelect = ({
  label,
  fieldName,
  testId,
  category,
  positions,
  className,
}: Ics204LocationSelectProps) => {
  const { control, setValue } = useFormContext();
  const currentValue =
    (useWatch({ control, name: fieldName }) as string | undefined) ?? '';
  const [addNewOpen, setAddNewOpen] = useState(false);
  const [draftText, setDraftText] = useState('');
  const [valueBeforeAddNew, setValueBeforeAddNew] = useState('');
  /** Skip restore when closing after a successful confirm. */
  const confirmedRef = useRef(false);

  const options = useMemo(() => {
    const names = new Set<string>();
    for (const position of positions) {
      if (position.isResourcePosition) continue;
      if (position.category !== category) continue;
      const name = position.name.trim();
      if (name) names.add(name);
    }
    const orphan = currentValue.trim();
    if (orphan && orphan !== ADD_NEW_VALUE) names.add(orphan);

    const rosterOptions = [...names]
      .sort((a, b) => a.localeCompare(b))
      .map<Ics204SelectOption>((name) => ({ value: name, label: name }));

    return [...rosterOptions, { value: ADD_NEW_VALUE, label: '+ Add New' }];
  }, [positions, category, currentValue]);

  const closeAddNew = (restorePrevious: boolean) => {
    setAddNewOpen(false);
    setDraftText('');
    if (restorePrevious) {
      setValue(fieldName, valueBeforeAddNew, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  };

  const confirmAddNew = () => {
    const trimmed = draftText.trim();
    if (!trimmed) return;
    confirmedRef.current = true;
    setValue(fieldName, trimmed, { shouldDirty: true, shouldValidate: true });
    setAddNewOpen(false);
    setDraftText('');
  };

  return (
    <>
      <Ics204SelectField
        label={label}
        fieldName={fieldName}
        testId={testId}
        className={className}
        options={options}
        placeholder="Select…"
        onSelectChange={(next, current) => {
          if (next !== ADD_NEW_VALUE) return;
          setValueBeforeAddNew(current);
          setDraftText('');
          setAddNewOpen(true);
          return false;
        }}
      />

      <AlertDialog
        open={addNewOpen}
        onOpenChange={(open) => {
          if (open) return;
          if (confirmedRef.current) {
            confirmedRef.current = false;
            setAddNewOpen(false);
            setDraftText('');
            return;
          }
          closeAddNew(true);
        }}
      >
        <AlertDialogContent
          className="dark"
          data-testid={`${testId}-add-new-dialog`}
        >
          <AlertDialogHeader>
            <AlertDialogTitle>
              Add {label} for this ICS-204 only
            </AlertDialogTitle>
            <AlertDialogDescription>
              The text you enter is saved on this Assignment List card only. It
              is not added to the incident roster. To create a real {category}{' '}
              seat, open the roster, add a position, and set its Category to{' '}
              {category}.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="tw:flex tw:flex-col tw:gap-1">
            <Label htmlFor={`${testId}-add-new-input`} className={ICS204_LABEL}>
              {label}
            </Label>
            <Input
              id={`${testId}-add-new-input`}
              data-testid={`${testId}-add-new-input`}
              value={draftText}
              onChange={(event) => setDraftText(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  confirmAddNew();
                }
              }}
              autoFocus
              placeholder={`Enter ${label.toLowerCase()}…`}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid={`${testId}-add-new-cancel`}
              onClick={() => closeAddNew(true)}
            >
              Cancel
            </AlertDialogCancel>
            <Button
              type="button"
              className={ICS204_OUTLINE_BTN}
              data-testid={`${testId}-add-new-confirm`}
              disabled={!draftText.trim()}
              onClick={confirmAddNew}
            >
              Use on this card
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default Ics204LocationSelect;
